/**
 * 2026-09-22 — Turkish operator labels for the login-failure reason codes the
 * API classifier emits (apps/api/src/routes/admin/login-failure-reason.ts).
 * Keep the code list in sync with that module.
 */

export type LoginFailureCode =
  | 'email_password_required'
  | 'auth_rejected_ambiguous'
  | 'wrong_password'
  | 'user_unknown'
  | 'wrong_host'
  | 'access_disabled'
  | 'auth_failed'
  | 'web_login_rejected'
  | 'mint_rejected'
  | 'no_password'
  | 'mtan_required'
  | 'login_flow_changed'
  | 'generic'
  | 'unknown'
  | 'other';

export interface LoginFailureReasonWire {
  readonly code: string;
  readonly detail: string | null;
  readonly at?: string | null;
}

export interface LoginFailureLabel {
  readonly label: string;
  readonly hint: string;
}

export const LOGIN_FAILURE_LABELS: Readonly<Record<LoginFailureCode, LoginFailureLabel>> = {
  email_password_required: {
    label: 'E-posta şifresi gerekli',
    hint:
      "t-online mail programı (IMAP/POP3) girişinde yalnız ayrı Telekom 'E-Mail-Passwort' kabul edilir. " +
      "Telekom Kundencenter'da oluşturup 'Şifre değiştir' ile girin (ya da girilen e-posta şifresi yanlış).",
  },
  auth_rejected_ambiguous: {
    label: 'Giriş reddedildi (şifre, IMAP izni veya IP engeli)',
    hint:
      'GMX yanlış şifre, kapalı IMAP izni ve proxy çıkış IP engeli için aynı cevabı verir; ' +
      'bu cevabı alan hesapların çoğu sonradan kendiliğinden düzeldi.',
  },
  wrong_password: { label: 'Şifre yanlış', hint: 'Sağlayıcı kullanıcı adı/şifre çiftini reddetti.' },
  user_unknown: { label: 'Hesap bulunamadı', hint: 'Sağlayıcı bu adresi tanımıyor.' },
  wrong_host: { label: 'Adres bu sunucuya ait değil', hint: 'Örn. sina.com ↔ vip.sina.com karışıklığı.' },
  access_disabled: { label: 'IMAP/POP3 erişimi kapalı', hint: 'Hesap ayarlarında mail programı erişimi açılmalı.' },
  auth_failed: { label: 'Kimlik doğrulama başarısız', hint: 'Sağlayıcı girişi reddetti.' },
  web_login_rejected: { label: 'Web girişi reddedildi', hint: 'Yenileme/alias girişinde reddedildi.' },
  mint_rejected: { label: 'Uygulama şifresi üretimi reddedildi', hint: '' },
  no_password: { label: 'Kayıtlı şifre yok', hint: '' },
  mtan_required: { label: 'SMS (mTAN) doğrulaması gerekli', hint: '' },
  login_flow_changed: { label: 'Giriş akışı ayrıştırılamadı', hint: 'Sağlayıcının giriş sayfası değişmiş olabilir.' },
  generic: { label: 'Giriş başarısız (ayrıntı yok)', hint: '' },
  unknown: { label: 'Neden kaydedilmedi', hint: '' },
  other: { label: 'Giriş reddedildi', hint: '' },
};

export function loginFailureLabel(code: string | null | undefined): LoginFailureLabel {
  if (code && code in LOGIN_FAILURE_LABELS) return LOGIN_FAILURE_LABELS[code as LoginFailureCode];
  return LOGIN_FAILURE_LABELS.other;
}

/** Tooltip text: hint + cleaned server reply, whichever exist. */
export function loginFailureTitle(reason: LoginFailureReasonWire | null | undefined): string {
  if (!reason) return '';
  const { hint } = loginFailureLabel(reason.code);
  return [hint, reason.detail ? `Sunucu: ${reason.detail}` : ''].filter(Boolean).join(' — ');
}
